import { useEffect } from "react";
import { useParams } from "react-router-dom";

/**
 * Public doctor page — Milestone B2 upgrade.
 *
 * Previously we redirected users to /api/public/dr/... which polluted the
 * address bar with an ugly API URL. Now we FETCH the SSR HTML into the
 * SPA shell and swap the document, keeping the clean /dr/... path in the
 * address bar for humans + link-sharing. Canonical tag (already prod-origin
 * via iter65) protects SEO regardless of which path is visible.
 *
 * Production recommendation: an ingress/proxy rewrite that serves the SSR
 * HTML directly at /dr/... is more robust (no client-side swap needed).
 * The Emergent preview ingress doesn't support that today, hence this
 * client-side approach.
 */
export default function PublicDoctorRedirect() {
    const { slug, articleSlug, page } = useParams();

    useEffect(() => {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const s = encodeURIComponent(slug);
        let target;
        if (articleSlug) {
            target = `${base}/api/public/dr/${s}/articles/${encodeURIComponent(articleSlug)}`;
        } else if (page) {
            target = `${base}/api/public/dr/${s}/${encodeURIComponent(page)}`;
        } else {
            target = `${base}/api/public/dr/${s}`;
        }
        // Preserve query string
        if (window.location.search) {
            target += window.location.search;
        }

        let cancelled = false;

        (async () => {
            try {
                const r = await fetch(target, { credentials: "omit" });
                if (r.status === 404) {
                    // Empty subpage or unpublished slug → server-side 404 page
                    document.title = "Not found";
                    document.body.innerHTML = await r.text();
                    return;
                }
                if (!r.ok) {
                    throw new Error(`SSR fetch failed: ${r.status}`);
                }
                const html = await r.text();
                if (cancelled) return;

                // Swap the entire document with the SSR HTML. document.open/
                // write/close re-executes inline <script> tags so the
                // analytics beacon, sticky nav, share popover, count-up JS
                // etc. all wire up correctly.
                document.open();
                document.write(html);
                document.close();
                // Address bar stays on /dr/{slug}/{page} — no history push
                // (we're already on the correct URL from React Router).
            } catch (e) {
                if (cancelled) return;
                // Last-resort fallback so users never end up on a blank page.
                window.location.replace(target);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [slug, articleSlug, page]);

    return (
        <div className="min-h-screen flex items-center justify-center text-[#64748B]" data-testid="public-redirect">
            Loading public profile…
        </div>
    );
}
